// FinKloz — Reconciliation Engine
// Classifies reconciling items and computes the standard bank rec format.
// Ported from backend/services/bank_rec.py with enhancements.

// ─── Classify unmatched items into standard bank rec categories ───
export function classifyReconcilingItems(unmatchedBank, unmatchedGL) {
  const items = [];

  for (const btxn of unmatchedBank) {
    const amount = (btxn.credit || 0) - (btxn.debit || 0);
    const desc = (btxn.description || '').toLowerCase();

    let type, action;

    if (/frais|fee|service\s*charge|frais\s*bancaire|bank\s*charge|monthly\s*fee/i.test(desc)) {
      type = 'bank_fee';
      action = 'book_je';
    } else if (/int[e\u00e9]r[e\u00ea]t|interest/i.test(desc)) {
      type = 'interest';
      action = 'book_je';
    } else if (/nsf|returned|rejected|rejet[e\u00e9]/i.test(desc)) {
      type = 'nsf';
      action = 'book_je';
    } else if (btxn.debit > 0) {
      type = 'outstanding_check';
      action = 'timing';
    } else if (btxn.credit > 0) {
      type = 'deposit_in_transit';
      action = 'timing';
    } else {
      type = 'unmatched';
      action = 'investigate';
    }

    items.push({
      id: btxn.id || `rec-bank-${items.length}`,
      source: 'bank',
      type,
      date: btxn.date,
      description: btxn.description || '',
      amount,
      action,
      note: '',
    });
  }

  for (const gl of unmatchedGL) {
    // Use txn amounts (original currency) when available
    const d = gl.txnDebit !== undefined ? gl.txnDebit : (gl.debit || 0);
    const c = gl.txnCredit !== undefined ? gl.txnCredit : (gl.credit || 0);
    const amount = d - c;

    let type;
    if (d > 0) {
      type = 'deposit_in_transit';
    } else {
      type = 'outstanding_check';
    }

    items.push({
      id: gl.id || `rec-gl-${items.length}`,
      source: 'gl',
      type,
      date: gl.date,
      description: gl.description || '',
      amount,
      action: 'timing',
      note: '',
    });
  }

  return items;
}

// ─── Compute standard bank reconciliation ───
export function computeReconciliation(bankClosingBalance, glEndingBalance, reconcilingItems) {
  // Bank side adjustments
  const outstandingChecks = reconcilingItems
    .filter(i => i.type === 'outstanding_check')
    .reduce((s, i) => s + Math.abs(i.amount), 0);

  const depositsInTransit = reconcilingItems
    .filter(i => i.type === 'deposit_in_transit' && i.source === 'gl')
    .reduce((s, i) => s + i.amount, 0);

  const adjustedBank = bankClosingBalance - outstandingChecks + depositsInTransit;

  // Book side adjustments
  const bankFees = reconcilingItems
    .filter(i => i.type === 'bank_fee')
    .reduce((s, i) => s + i.amount, 0);

  const interest = reconcilingItems
    .filter(i => i.type === 'interest')
    .reduce((s, i) => s + i.amount, 0);

  const nsfItems = reconcilingItems
    .filter(i => i.type === 'nsf')
    .reduce((s, i) => s + i.amount, 0);

  const otherAdj = reconcilingItems
    .filter(i => i.type === 'unmatched' && i.source === 'bank')
    .reduce((s, i) => s + i.amount, 0);

  const adjustedBook = glEndingBalance + bankFees + interest + nsfItems + otherAdj;

  const difference = Math.round((adjustedBank - adjustedBook) * 100) / 100;

  return {
    bankBalance: bankClosingBalance,
    outstandingChecks: Math.round(outstandingChecks * 100) / 100,
    depositsInTransit: Math.round(depositsInTransit * 100) / 100,
    adjustedBankBalance: Math.round(adjustedBank * 100) / 100,

    glBalance: glEndingBalance,
    bankFees: Math.round(bankFees * 100) / 100,
    interest: Math.round(interest * 100) / 100,
    nsfItems: Math.round(nsfItems * 100) / 100,
    otherAdjustments: Math.round(otherAdj * 100) / 100,
    adjustedBookBalance: Math.round(adjustedBook * 100) / 100,

    difference,
    isReconciled: Math.abs(difference) < 1.00,
  };
}

// ─── Generate proposed journal entries from reconciling items ───
export function generateProposedJEs(reconcilingItems, accountNo) {
  const jes = [];

  for (const item of reconcilingItems) {
    if (item.action !== 'book_je') continue;

    let debitAccount, creditAccount, description;

    if (item.type === 'bank_fee') {
      debitAccount = '65010'; // Bank Charges expense
      creditAccount = accountNo;
      description = `Bank fee: ${item.description}`;
    } else if (item.type === 'interest') {
      debitAccount = accountNo;
      creditAccount = '49400'; // Interest Income
      description = `Interest earned: ${item.description}`;
    } else if (item.type === 'nsf') {
      debitAccount = '12100'; // Accounts Receivable
      creditAccount = accountNo;
      description = `NSF cheque: ${item.description}`;
    } else {
      debitAccount = '29999'; // Suspense
      creditAccount = accountNo;
      description = `Unclassified: ${item.description}`;
    }

    jes.push({
      id: `je-${jes.length}`,
      date: item.date,
      description,
      debitAccount,
      creditAccount,
      amount: Math.abs(item.amount),
      type: item.type,
      sourceItemId: item.id,
      approved: false,
    });
  }

  return jes;
}

// ─── FX Conversion for USD accounts ───
export function computeFXConversion(usdEndingBalance, fxRate, glCADEndingBalance) {
  const convertedCAD = Math.round(usdEndingBalance * fxRate * 100) / 100;
  const fxJEAmount = Math.round((convertedCAD - glCADEndingBalance) * 100) / 100;

  return {
    usdEnding: usdEndingBalance,
    fxRate,
    convertedCAD,
    glCADEnding: glCADEndingBalance,
    fxJEAmount,
  };
}

// ─── Run full reconciliation pipeline ───
export function runReconciliation(statementData, glData, accountNo) {
  // Import matching engine
  const { matchTransactions } = require('./matchingEngine');

  const matchResult = matchTransactions(statementData.transactions, glData.entries);

  const reconcilingItems = classifyReconcilingItems(
    matchResult.unmatchedBank,
    matchResult.unmatchedGL
  );

  const reconciliation = computeReconciliation(
    statementData.metadata.closingBalance,
    glData.endingBalance,
    reconcilingItems
  );

  const proposedJEs = generateProposedJEs(reconcilingItems, accountNo);

  return {
    matchResult,
    reconcilingItems,
    reconciliation,
    proposedJEs,
  };
}
